// Claude: Seller Browse RFXs — wired to GET /rfx/browse 24-02-2026
// Claude: Added Keywords tab — shows keyword matches read-only 10-03-2026
import React, { useEffect, useState } from "react";
import {
  Box,
  Flex,
  Text,
  Badge,
  Checkbox,
  HStack,
} from "@chakra-ui/react";
import { MdOutlineAccessTime, MdOutlineChevronRight } from "react-icons/md";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import actions from "../../redux/Actions";
import { Sellerscreens } from "../../Constants/data";
import { RfxAPI } from "../../Apis/rfxAPI";
import { SellerAPI } from "../../Apis/sellerAPI";
import { useToastWrapper } from "../../Wrapper/toastWrapper";
import { errorHandler } from "../../Utils/handler";
import moment from "moment";
import Loader from "../../Components/Loader/Loader";
import Jumbotron from "../../Components/Common/Jumbotron";

// Claude: seller response status badge map — submitted removed 18-04-2026
const statusBadge: Record<string, { label: string; colorScheme: string } | null> = {
  interested: { label: "Interested", colorScheme: "blue" },
  revision_requested: { label: "Revision Requested", colorScheme: "orange" },
  not_interested: { label: "Not Interested", colorScheme: "gray" },
};

// Claude: tab styles 10-03-2026
const TAB_ACTIVE_BG = "#0E122F";
const TAB_INACTIVE_BG = "#E8E8F0";
const KEYWORDS_BORDER = "#A5C8F0";
const RFX_BORDER = "#FFD4B2";

const RFXList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { error } = useToastWrapper();
  const { verificationStatus } = useSelector((state: any) => state.user);
  const { notification: { unreadNotifications } } = useSelector((state: any) => state);
  const sellerVerificationStatus = useSelector((state: any) => state.user?.verificationStatus);
  const [profile, setProfile] = React.useState<any>(null);
  // Claude: Buyer Queries tab shown first by default 26-03-2026
  const [activeTab, setActiveTab] = useState<"rfx" | "keywords">("rfx");
  const activeBorder = activeTab === "keywords" ? KEYWORDS_BORDER : RFX_BORDER;
  const [rfxs, setRfxs] = useState<any[]>([]);
  const [keywordMatches, setKeywordMatches] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [hideNotInterested, setHideNotInterested] = useState(true);

  const fetchRfxs = async () => {
    setLoading(true);
    try {
      const res = await RfxAPI.browseRfxs();
      setRfxs(res?.data ?? []);
    } catch (err) {
      error(errorHandler(err));
    } finally {
      setLoading(false);
    }
  };

  const fetchKeywordMatches = async () => {
    setLoading(true);
    try {
      const res = await RfxAPI.getSellerKeywordMatches();
      setKeywordMatches(res?.data ?? []);
    } catch (err) {
      error(errorHandler(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    dispatch(actions.sellerMenu.set({ activeMenu: Sellerscreens.RFX }));
    fetchRfxs();
    fetchKeywordMatches();
    // Claude: N47 — fetch profile for Jumbotron 27-03-2026
    SellerAPI.getSellerProfile().then((res: any) => setProfile(res?.data?.data)).catch(() => {});
    // Claude: rescanRFXforSeller disabled — redundant after interested gate enforced 08-05-2026
    // RfxAPI.rescanRFXforSeller();
  }, []);

  if (loading) return <Loader />;

  return (
    <Box w="100%">
      {/* Claude: N47 — Jumbotron replaces dark header (landing page) 27-03-2026 */}
      <Jumbotron
        personName={profile?.personName}
        companyName={profile?.companyName}
        unreadNotifications={unreadNotifications}
        role="Seller"
        navigateUrl="/seller/notifications"
        profileNavigateUrl="/seller/myaccount"
        linkedinPhoto={profile?.linkedinPhoto}
        verificationStatus={sellerVerificationStatus}
        visibilityMode={profile?.visibilityMode}
        isPlanActive={profile?.isPlanActive}
        hasSubscriptionPlan={!!profile?.subscriptionPlanId}
      />
        {/* Claude: Tab bar — Buyer Queries left, Keywords right 26-03-2026 */}
        <HStack spacing={0} pt="16px">
          <Box
            px={5} py={2}
            borderRadius="0"
            cursor="pointer"
            bg={activeTab === "rfx" ? TAB_ACTIVE_BG : TAB_INACTIVE_BG}
            color={activeTab === "rfx" ? "white" : "gray.600"}
            fontSize="sm" fontWeight={activeTab === "rfx" ? 600 : 400}
            border="1px solid"
            borderColor={activeTab === "rfx" ? TAB_ACTIVE_BG : "gray.200"}
            borderBottom={activeTab === "rfx" ? "none" : `1px solid ${activeBorder}`}
            mb={activeTab === "rfx" ? "-1px" : "0"}
            onClick={() => {
              // Claude: provisional sellers cannot access Buyer Queries tab 21-03-2026
              if (verificationStatus === "provisional") { error("Profile is pending review and has provisional access only."); return; }
              setActiveTab("rfx");
            }}
          >
            Buyer Queries ({rfxs.length})
          </Box>
          {/* Claude: Keywords tab hidden — deferred until keyword gap analysis is ready 10-04-2026
          <Box
            px={5} py={2}
            borderRadius="0"
            cursor="pointer"
            bg={activeTab === "keywords" ? TAB_ACTIVE_BG : TAB_INACTIVE_BG}
            color={activeTab === "keywords" ? "white" : "gray.600"}
            fontSize="sm" fontWeight={activeTab === "keywords" ? 600 : 400}
            border="1px solid"
            borderColor={activeTab === "keywords" ? TAB_ACTIVE_BG : "gray.200"}
            borderBottom={activeTab === "keywords" ? "none" : `1px solid ${activeBorder}`}
            mb={activeTab === "keywords" ? "-1px" : "0"}
            onClick={() => setActiveTab("keywords")}
            ml={1}
          >
            Keywords ({keywordMatches.filter((km: any) => (km.matchedKeywords ?? []).length > 0).length})
          </Box>
          */}
        </HStack>

        {/* Claude: bordered panel connected to active tab 11-03-2026 */}
        <Box
          px={3}
          pb="30px"
          pt={4}
        >

          {/* ── RFX TAB ── */}
          {activeTab === "rfx" && (
            <>
              {/* Claude: RFX limit reached banner — no new opportunities but existing ones still actionable 23-04-2026 */}
              {profile && profile.rfxLimit !== null && profile.rfxLimit !== -1 &&
                (profile.rfxMatchedCountPerCycle ?? 0) >= profile.rfxLimit && (
                <Box mb={3} px={4} py={3} bg="#FFF3CD" border="1px solid #FFEAA7" borderRadius="8px">
                  <Text fontSize="13px" color="#856404" fontWeight={600}>Buyer Inquiry Limit Reached</Text>
                  <Text fontSize="12px" color="#856404" mt={1}>
                    No new buyer inquiries will be matched to you this cycle. You can still mark your existing opportunities as interested or not interested.
                  </Text>
                </Box>
              )}
              {/* Claude: soft verification nudge — browse allowed, respond is gated in RFXDetail 03-03-2026 */}
              {(verificationStatus === "unverified" || verificationStatus === "resubmitted") && (
                <Text mt={4} mb={1} fontSize="xs" color="#C62828" textAlign="center">
                  Profile verification required to respond.{" "}
                  <Text as="span" textDecoration="underline" cursor="pointer" onClick={() => navigate("/seller/profile")}>
                    Update profile
                  </Text>
                </Text>
              )}
              {/* Claude: Hide Not Interested/Ignored Buyers checkbox 09-04-2026 */}
              <Flex justifyContent="flex-end" mb={2}>
                <Checkbox
                  size="sm"
                  isChecked={hideNotInterested}
                  onChange={(e) => setHideNotInterested(e.target.checked)}
                  colorScheme="gray"
                >
                  <Text fontSize="10px" color="gray.500">Hide Not Interested/Ignored Buyers</Text>
                </Checkbox>
              </Flex>
              {rfxs.length === 0 && (
                <Flex direction="column" alignItems="center" mt={12} px={4}>
                  <Text fontSize="sm" color="gray.400" textAlign="center">
                    No open RFXs right now — do check back soon.
                  </Text>
                </Flex>
              )}
              {rfxs
                .filter((rfx: any) => hideNotInterested ? rfx.mySellerResponseStatus !== "not_interested" && rfx.buyerRelevanceStatus !== "Not_Relevant" : true)
                .map((rfx: any, i: number) => {
                const badge = rfx.mySellerResponseStatus ? statusBadge[rfx.mySellerResponseStatus] : null;
                return (
                  <Box
                    key={rfx._id}
                    mt={i === 0 ? 1 : 4}
                    p="16px"
                    border="1px solid #D2D2D2"
                    borderRadius="15px"
                    shadow="sm"
                    bg="white"
                    onClick={() => navigate("/seller/rfx-detail", { state: { rfxId: rfx._id, humanRfxId: rfx.rfxId, isPlanActive: profile?.isPlanActive } })}
                    cursor="pointer"
                    _hover={{ shadow: "md" }}
                  >
                    {/* Row 1: RFX ID · person · company · expiry · chevron */}
                    <Flex alignItems="center" gap="5px" mb={1} flexWrap="nowrap">
                      <Text fontSize="9px" color="gray.400" flexShrink={0}>{rfx.rfxId}</Text>
                      <Text fontSize="9px" color="gray.300" flexShrink={0}>·</Text>
                      <HStack spacing="3px" flexShrink={0}>
                        <MdOutlineAccessTime size={10} color="#aaa" />
                        <Text fontSize="9px" color="gray.400">{moment(rfx.expiryDate).format("DD MMM YYYY")}</Text>
                      </HStack>
                      <Text fontSize="9px" color="gray.300" flexShrink={0}>·</Text>
                      <Text fontSize="9px" color="gray.400" noOfLines={1} flex={1} minW={0}>
                        {rfx.buyerId?.buyerCompanyName}
                      </Text>
                      <Box flexShrink={0}><MdOutlineChevronRight size={14} color="#aaa" /></Box>
                    </Flex>
                    {rfx.republishedFromRfxId && (
                      <Text fontSize="9px" color="purple.500" mb={1}>Republished from {rfx.republishedFromRfxId}</Text>
                    )}
                    {rfx.republishedToRfxId && (
                      <Text fontSize="9px" color="purple.500" mb={1}>Republished as {rfx.republishedToRfxId}</Text>
                    )}
                    {/* Row 2: RFX details + badges */}
                    <Flex justifyContent="space-between" alignItems="flex-start">
                      <Text fontSize="sm" fontWeight="semibold" flex={1} pr={2} lineHeight="1.4" noOfLines={2}>
                        {rfx.rfxDetails}
                      </Text>
                      <HStack spacing={1} flexShrink={0}>
                        {badge && (
                          <Badge colorScheme={badge.colorScheme} textTransform="none" fontSize="10px">
                            {badge.label}
                          </Badge>
                        )}
                        {rfx.status === "republished" && (
                          <Badge colorScheme="gray" textTransform="none" fontSize="10px">
                            Closed (republished)
                          </Badge>
                        )}
                      </HStack>
                    </Flex>
                    {/* Claude: department + tags fine print 17-Apr-2026 */}
                    {(() => {
                      const deptNames = rfx.departments?.length
                        ? rfx.departments.map((d: any) => (typeof d === "object" ? d.name : d)).join(", ")
                        : null;
                      const tags = rfx.targetTags?.length ? rfx.targetTags.join(", ") : null;
                      const parts = [deptNames ? `Dept: ${deptNames}` : null, tags ? `Tags: ${tags}` : null].filter(Boolean);
                      return parts.length > 0 ? (
                        <Text fontSize="9px" color="gray.400" mt={1}>{parts.join("  ·  ")}</Text>
                      ) : null;
                    })()}
                  </Box>
                );
              })}
            </>
          )}

          {/* ── KEYWORDS TAB ── */}
          {/* Claude: consolidated deduplicated keyword list across all matched RFPs 10-04-2026 */}
          {activeTab === "keywords" && (() => {
            const allKeywords = Array.from(new Set(
              keywordMatches
                .flatMap((km: any) => km.matchedKeywords ?? [])
                .map((k: string) => k.trim().toLowerCase())
                .filter(Boolean)
            )).sort() as string[];
            return allKeywords.length === 0 ? (
              <Flex direction="column" alignItems="center" mt={12} px={4}>
                <Text fontSize="sm" color="gray.400" textAlign="center">
                  No keyword matches yet — keep your profile and assets up to date.
                </Text>
              </Flex>
            ) : (
              <Flex flexWrap="wrap" gap={2} mt={3}>
                {allKeywords.map((kw) => (
                  <Badge key={kw} px={2} py={1} borderRadius="md" fontSize="11px" textTransform="none" colorScheme="purple" variant="subtle">
                    {kw}
                  </Badge>
                ))}
              </Flex>
            );
          })()}

        </Box>
    </Box>
  );
};

export default RFXList;
